/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from "react";
import {
  Animated,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  Image,
  Alert,
  Button,
  ActivityIndicator,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import store, { RootState, AppDispatch } from "../../store";
import { createApiClient, apiClient, getLocalTimestamp } from "../../api/apiClient";
import {
  setBehaviourData,
  setQuestionData,
  setSelectedWalk,
} from "../../store/slices/sparcWalkSlice";
import * as sparcWalkInspectionActions from "../../store/slices/sparcWalkInspectionSlice";
import { useToast } from "../../components/LhToastContainer";
import { SparcWalkRowData } from "../../store/slices/sparcWalkSlice";
import SparcWalkInspectionHeader from "./SparcWalkInspectionHeader";
import SparcWalkInspectionQuestions from "./SparcWalkInspectionQuestions";
import SparcWalkInspectionMiscellaneous from "./SparcWalkInspectionMiscellaneous";
import SparcWalkInspectionAction from "./SparcWalkInspectionAction";
import LHLoader from "../../components/LHLoader";
import SparcWalkInspectionExpectedAnswers from "./SparcWalkInspectionExpectedAnswers";
import SparcWalkInspectionCompleteSection from "./SparcWalkInspectionCompleteSection";
import SparcWalkInspectionObservations from "./SparcWalkInspectionObservations";
import SparcWalkInspectionPosibleSolutions from "./SparcWalkInspectionPosibleSolutions";
import SparcWalkInspectionActivity from "./SparcWalkInspectionActivity";
import SparcWalkSignature from "./SparcWalkSignature";
import SparcWalkErrorModal from "./components/SparcWalkErrorModal";
import { DropdownItem } from "../../types/sparcWalk";
import FontFamily from "../../constants/fonts";
import { useSelectedLocation } from "../../hooks";
import {
  handleSparcWalkErrorWithFriendlyMessage,
  SPARC_WALK_API_NAMES,
} from "../../store/utils/sparcWalkErrorHandler";
import { useTheme } from "../../theme";

interface Employee {
  First_Name: string;
  Last_Name: string;
  GPID: string;
  Id?: string;
  Active_vs_Inactive?: string;
}

interface QuestionItem {
  ID: number;
  Id?: number;
  id?: string;
  Question: string;
  Negative_Behavior?: string;
  Details?: string;
  Detail_Link?: string;
}

interface StatusMessage {
  type: "success" | "error";
  message: string;
}

interface SparcWalkInspectionScreenProps {
  selectedZone: DropdownItem;
  selectedCategory: DropdownItem;
  onBackPress?: () => void;
  onNavigateHome?: () => void;
  selectedWalkData?: SparcWalkRowData;
  isDisabled?: boolean;
  onSubmitSuccess?: (id: number | string, status: string) => void;
}

const SparcWalkInspectionScreen: React.FC<SparcWalkInspectionScreenProps> = ({
  selectedZone,
  selectedCategory,
  onBackPress,
  onNavigateHome,
  selectedWalkData,
  isDisabled = false,
  onSubmitSuccess,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const scrollViewRef = useRef<ScrollView>(null);
  const scrollToTopButtonRef = useRef<any>(null);
  const dispatch = useDispatch<AppDispatch>();

  const toast = useToast();
  const selectedLocation = useSelectedLocation();

  // Store initial location ID to detect changes after mount
  const [initialLocationId] = useState(selectedLocation?.id);

  // Detect location changes and redirect back to SparcWalkScreen
  useEffect(() => {
    if (initialLocationId && selectedLocation?.id && initialLocationId !== selectedLocation.id) {
      if (onBackPress) {
        onBackPress();
      }
    }
  }, [selectedLocation?.id, initialLocationId, onBackPress]);

  // Reusable function to scroll to top of the screen
  const scrollToTop = () => {
    try {
      // For web platform, scroll to top of document
      if (Platform.OS === "web") {
        // @ts-ignore - Access to window object in web platform
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth",
        });

        // Additional fallback for web, in case the scrollViewRef is available
        if (scrollViewRef.current) {
          setTimeout(() => {
            scrollViewRef.current?.scrollTo({ x: 0, y: 0, animated: true });
          }, 100);
        }
      }
      // For native platforms, use the scrollView reference
      else if (scrollViewRef.current) {
        scrollViewRef.current.scrollTo({
          x: 0,
          y: 0,
          animated: true,
        });
      }

      console.log("Scrolling to top executed");
    } catch (scrollError) {
      console.error("Error scrolling to top:", scrollError);
    }
  };

  // Define a type that represents both possible structures of employee data
  interface EmployeeDataStructure {
    data?: Employee[];
  }
  interface EmployeeDataStructure {
    data?: Employee[];
  }

  const employeesData = useSelector(
    (state: RootState) => state.appData.employees
  );

  // Log the actual structure to help with debugging
  useEffect(() => {
    console.log("Raw employeesData from Redux:", {
      type: typeof employeesData,
      isArray: Array.isArray(employeesData),
      hasDataProperty:
        typeof employeesData === "object" &&
        employeesData !== null &&
        "data" in employeesData,
      structure: employeesData,
    });
  }, [employeesData]);

  // Properly handle the employees data structure which might be {data: Employee[]} or directly Employee[]
  const employees = React.useMemo(() => {
    if (Array.isArray(employeesData)) {
      return employeesData as Employee[];
    } else if (
      employeesData &&
      typeof employeesData === "object" &&
      "data" in employeesData
    ) {
      return ((employeesData as EmployeeDataStructure).data ||
        []) as Employee[];
    } else {
      return [] as Employee[];
    }
  }, [employeesData]);

  const userEmail = useSelector(
    (state: RootState) => state.auth.user?.username
  );
  const periodWeek = useSelector(
    (state: RootState) => state.appData.periodWeek
  );
  const selectedWalk = useSelector(
    (state: RootState) => state.sparcWalk.selectedWalk
  );

  const sparcWalkRows = useSelector(
    (state: RootState) => state.sparcWalk.sparcWalkRows
  );

  const zone = useSelector((state: RootState) => state.sparcWalk.zone);

  const behaviourData = useSelector(
    (state: RootState) => state.sparcWalk.behaviourData
  );

  // Main component state
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<{
    id: string;
    displayText: string;
  } | null>(null);
  const [questionItems, setQuestionItems] = useState<QuestionItem[]>([]);
  const [selectedBehaviorIds, setSelectedBehaviorIds] = useState<string[]>([]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>([]);
  const [expectedAnswers, setExpectedAnswers] = useState("");
  const [observations, setObservations] = useState("");
  const [possibleSolutions, setPossibleSolutions] = useState("");
  const [miscNotes, setMiscNotes] = useState("");
  const [_managerSignature, setManagerSignature] = useState<string | null>(
    null
  );
  const [_selectedStatus, _setSelectedStatus] = useState<string | null>(null);
  
  // State for scroll to top button
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const scrollButtonOpacity = useRef(new Animated.Value(0)).current;

  console.log("selectedZone", selectedZone);

  useEffect(() => {
    console.log("selectedWalk", selectedWalk);
    if (sparcWalkRows) {
      sparcWalkRows.forEach((row: SparcWalkRowData) => {
        if (row.ID === selectedWalk?.walkData?.id) {
          _setSelectedStatus(row.status);
        }
      });
    }
  }, [sparcWalkRows, selectedWalk]);

  // Load initial data from Redux store if available
  // Note: This useEffect runs before our main data loading effect
  // For in-progress walks, the selectedWalk data will take priority over this initial state
  useEffect(() => {
    // Get the global state from Redux
    const sparcWalkInspectionState = (state: RootState) =>
      state.sparcWalk.selectedWalk || state.sparcWalkInspection;
    console.log("sparcWalkInspectionState", sparcWalkInspectionState);
    const storeState = sparcWalkInspectionState(store.getState());

    // Check if this is an in-progress walk (prioritize API data over local Redux state)
    const isInProgress =
      selectedWalk?.walkData?.status?.toLowerCase() === "in progress";

    // Only sync with Redux state for new walks or if we don't have selectedWalk data
    if (selectedWalk && storeState && !isInProgress) {
      // For non-in-progress walks, we can use the Redux store data
      if (storeState.selectedBehaviorIds?.length > 0) {
        setSelectedBehaviorIds(storeState.selectedBehaviorIds);
      }
      if (storeState.selectedQuestionIds?.length > 0) {
        setSelectedQuestionIds(storeState.selectedQuestionIds);
      }
      if (storeState.expectedAnswers) {
        setExpectedAnswers(storeState.expectedAnswers);
      }
      if (storeState.observations) {
        setObservations(storeState.observations);
      }
      if (storeState.possibleSolutions) {
        setPossibleSolutions(storeState.possibleSolutions);
      }
      if (storeState.selectedEmployeeId && employees?.length) {
        const employee = employees.find(
          (e: Employee) =>
            e.GPID === storeState.selectedEmployeeId ||
            e.Id === storeState.selectedEmployeeId
        );
        if (employee) {
          setSelectedEmployee({
            id: employee.GPID || employee.Id || "",
            displayText: `${employee.First_Name} ${employee.Last_Name}`,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWalk]);

  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(
    null
  );
  const [showValidation, setShowValidation] = useState(false);
  
  // Track if questions have been fetched to prevent duplicate API calls
  const questionsFetchedRef = useRef<string | false>(false);
  
  const [walkState, setWalkState] = useState<
    "new" | "in-progress" | "completed"
  >(() => {
    // Check selectedWalkData first, then fall back to selectedWalk from Redux
    // This prevents initializing as "new" when we actually have existing walk data
    const walkData = selectedWalkData || store.getState().sparcWalk.selectedWalk?.walkData;
    
    if (!walkData) {
      return "new";
    }
    return walkData.status?.toLowerCase() === "completed"
      ? "completed"
      : "in-progress";
  });

  // Sync initial walkState with Redux on component mount
  useEffect(() => {
    dispatch(sparcWalkInspectionActions.setWalkState(walkState));
  }, [dispatch, walkState]);

  // Handle scroll events to show/hide scroll to top button
  const handleScroll = (event: any) => {
    const { contentOffset } = event.nativeEvent;
    const shouldShow = contentOffset.y > 200; // Show button after scrolling 200px
    
    if (shouldShow !== showScrollToTop) {
      setShowScrollToTop(shouldShow);
      
      Animated.timing(scrollButtonOpacity, {
        toValue: shouldShow ? 1 : 0,
        duration: 200,
        useNativeDriver: true,
      }).start();
    }
  };

  // Handle scroll events for web
  useEffect(() => {
    if (Platform.OS === "web") {
      const handleWebScroll = () => {
        const shouldShow = window.scrollY > 200;
        
        if (shouldShow !== showScrollToTop) {
          setShowScrollToTop(shouldShow);
          
          Animated.timing(scrollButtonOpacity, {
            toValue: shouldShow ? 1 : 0,
            duration: 200,
            useNativeDriver: true,
          }).start();
        }
      };

      window.addEventListener('scroll', handleWebScroll);
      return () => window.removeEventListener('scroll', handleWebScroll);
    }
  }, [showScrollToTop, scrollButtonOpacity]);

  // Apply fixed positioning for web button to move with scrollbar
  useEffect(() => {
    if (Platform.OS === "web" && scrollToTopButtonRef.current) {
      // For web, we can directly access and modify the style of the DOM element
      // using the style prop because it will be rendered as a DOM element
      const buttonElement = scrollToTopButtonRef.current;
      
      // Check if we have access to the DOM node
      if (buttonElement) {
        // Create a custom CSS class for fixed positioning
        const styleTag = document.createElement('style');
        styleTag.innerHTML = `
          .scroll-to-top-fixed {
            position: fixed !important;
            bottom: 20px !important;
            right: 20px !important;
            z-index: 1000 !important;
          }
        `;
        document.head.appendChild(styleTag);
        
        // Apply the class to our button when it's rendered in the DOM
        // Use setTimeout to ensure the DOM element is rendered
        setTimeout(() => {
          const element = document.querySelector('[data-testid="scroll-to-top-button"]');
          if (element) {
            element.classList.add('scroll-to-top-fixed');
          }
        }, 100);
        
        return () => {
          document.head.removeChild(styleTag);
        };
      }
    }
  }, []);

  // Determine if form should be in read-only mode
  const isReadOnly = walkState === "completed" || isDisabled;

  const showStatusMessage = (type: "success" | "error", message: string) => {
    setStatusMessage({ type, message });

    // When showing an error, scroll to top immediately
    if (type === "error") {
      scrollToTop();
    }

    // Auto-clear messages after 5 seconds
    setTimeout(() => setStatusMessage(null), 5000);
  };
  // Format employees data for dropdown
  const formattedEmployeeOptions = React.useMemo(() => {
    // Add debug logging to understand the structure
    console.log("employees structure:", {
      type: typeof employees,
      isArray: Array.isArray(employees),
      length: employees?.length,
    });

    if (!employees || !Array.isArray(employees)) {
      return [];
    }

    return employees
      .filter((emp: Employee) => emp.Active_vs_Inactive === "Active")
      .map((emp: Employee) => ({
        displayText: `${emp.First_Name || ""} ${emp.Last_Name || ""}`.trim() || emp.GPID || "Unknown",
        id: String(emp.GPID || emp.Id || ""),
      }));
  }, [employees]);

  useEffect(() => {
    const fetchQuestions = async () => {
      if (!selectedCategory || !selectedZone) {
        return;
      }
      
      // Create a unique key for this fetch to prevent duplicate calls
      const fetchKey = `${selectedCategory.displayText}-${selectedZone.displayText}-${walkState}`;
      
      // If questions have already been fetched for this combination, skip
      if (questionsFetchedRef.current === fetchKey) {
        console.log('[SparcWalk] Questions already fetched for:', fetchKey);
        return;
      }
      
      setIsLoading(true);
      try {
        const category = encodeURIComponent(selectedCategory.displayText);
        const zoneId = Number(selectedZone.displayText?.charAt(0));
        const api = createApiClient();
        
        // new        -> org context only
        // in-progress -> org context + recordId
        // completed   -> skip org context
        const existingWalkId = walkState === 'in-progress' ? (selectedWalkData?.id ?? selectedWalk?.id ?? null) : null;
        const recordIdParam = existingWalkId ? `&recordId=${existingWalkId}` : '';
        const requestConfig = walkState === 'completed' ? { _skipOrgContextForQuestions: true } : undefined;

        console.log('[SparcWalk] Fetching questions - walkState:', walkState, 'recordId:', existingWalkId);

        const json = await apiClient(
          api,
          `/sparc-walks/questions?frequency=Daily&sparcCategory=${category}&walkType=SPARC%20Walk&zoneId=${zoneId}${recordIdParam}`,
          "GET",
          undefined,
          requestConfig
        );

        console.log(
          "API response for questions:",
          JSON.stringify(json, null, 2)
        );
        
        // Validate response
        if (!json || (!json.behaviour && !json.question)) {
          throw new Error("Invalid questions data received");
        }

        // Map behaviour data with proper IDs
        const mappedBehaviourData = (json.behaviour || []).map(
          (item: QuestionItem, i: number) => {
            // Ensure we have a valid ID
            console.log("Mapping behaviour item:", item);
            const QuestionId =
              item.ID !== undefined && item.ID !== null
                ? String(item.ID)
                : item.Id !== undefined && item.Id !== null
                ? String(item.Id)
                : String(Date.now());
            return {
              id: i,
              QuestionId: QuestionId, // Keep original ID
              Question: item.Negative_Behavior || item.Question, // Use Negative_Behavior if available, fallback to Question
              Details: item.Details || null,
              Detail_Link: item.Detail_Link || null,
            };
          }
        );

        // Map question data with proper IDs
        const mappedQuestionData = (json.question || []).map(
          (item: QuestionItem, i: number) => {
            // Ensure we have a valid ID
            const QuestionId =
              item.ID !== undefined && item.ID !== null
                ? String(item.ID)
                : item.Id !== undefined && item.Id !== null
                ? String(item.Id)
                : String(Date.now());
            return {
              id: i,
              QuestionId: QuestionId, // Keep original ID
              Question: item.Question,
              Details: item.Details || null,
              Detail_Link: item.Detail_Link || null,
            };
          }
        );

        console.log("Mapped behavior data with IDs:", mappedBehaviourData);
        console.log("Mapped question data with IDs:", mappedQuestionData);

        // Dispatch both behaviour and question data to the global store
        dispatch(setBehaviourData(mappedBehaviourData));
        dispatch(setQuestionData(mappedQuestionData));

        // Also update local state for the component
        setQuestionItems(mappedQuestionData);
        
        // Mark questions as fetched for this combination
        questionsFetchedRef.current = fetchKey;
      } catch (e) {
        console.error("Error fetching questions:", e);
        
        // Use centralized error handler with modal overlay
        handleSparcWalkErrorWithFriendlyMessage(
          SPARC_WALK_API_NAMES.FETCH_QUESTIONS,
          e,
          true, // Show modal overlay
          {
            category: selectedCategory?.displayText,
            zone: selectedZone?.displayText,
          }
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchQuestions();
    // Fixed: Removed 'dispatch' from dependencies (it's stable in Redux)
    // Fixed: Use only the stable string values instead of object references to prevent infinite rerenders
    // Added walkState to refetch questions if walk state changes (e.g., new -> in-progress)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory?.displayText, selectedZone?.displayText, walkState]);

  // Sync state changes to Redux store
  useEffect(() => {
    // Every time the local state changes, update the Redux store
    dispatch(
      sparcWalkInspectionActions.setSelectedBehaviorIds(selectedBehaviorIds)
    );
    dispatch(
      sparcWalkInspectionActions.setSelectedQuestionIds(selectedQuestionIds)
    );
    dispatch(sparcWalkInspectionActions.setExpectedAnswers(expectedAnswers));
    dispatch(sparcWalkInspectionActions.setObservations(observations));
    dispatch(
      sparcWalkInspectionActions.setPossibleSolutions(possibleSolutions)
    );

    // For the employee, we need to extract the ID
    if (selectedEmployee?.id) {
      dispatch(
        sparcWalkInspectionActions.setSelectedEmployeeId(selectedEmployee.id)
      );
    }

    // Sync miscellaneous observations with the sparcWalkInspectionSlice
    dispatch(
      sparcWalkInspectionActions.setMiscellaneousObservations(miscNotes)
    );
  }, [
    dispatch,
    selectedBehaviorIds,
    selectedQuestionIds,
    expectedAnswers,
    observations,
    possibleSolutions,
    selectedEmployee,
    miscNotes,
  ]);

  // Load data when the selected walk changes
  useEffect(() => {
    if (selectedWalk) {
      // Always make sure we have the complete walk data in sparcWalkInspection
      // This ensures all API data is available in the sparcWalkInspection state
      if (selectedWalk.walkData) {
        console.log(
          "Syncing complete walk data to sparcWalkInspection",
          selectedWalk
        );
        // Store complete API response data
        syncCompleteWalkDataToInspection(selectedWalk.walkData);
      }

      // Check if the walk is in-progress (not completed and not new)
      const isInProgress =
        selectedWalk.walkData?.status?.toLowerCase() === "in progress";

      if (isInProgress) {
        // Already synced complete data with syncCompleteWalkDataToInspection
        return; // Exit early for in-progress walks
      }

      // For new or completed walks, check if we already have data in Redux store
      const sparcWalkInspectionState = store.getState().sparcWalkInspection;

      // If we have data in the Redux store, use that (could be from a previous session)
      if (
        sparcWalkInspectionState.selectedBehaviorIds &&
        sparcWalkInspectionState.selectedBehaviorIds.length > 0
      ) {
        setSelectedBehaviorIds(sparcWalkInspectionState.selectedBehaviorIds);
      }

      if (
        sparcWalkInspectionState.selectedQuestionIds &&
        sparcWalkInspectionState.selectedQuestionIds.length > 0
      ) {
        setSelectedQuestionIds(sparcWalkInspectionState.selectedQuestionIds);
      }

      if (sparcWalkInspectionState.expectedAnswers) {
        setExpectedAnswers(sparcWalkInspectionState.expectedAnswers);
      }

      if (sparcWalkInspectionState.observations) {
        setObservations(sparcWalkInspectionState.observations);
      }

      if (sparcWalkInspectionState.possibleSolutions) {
        setPossibleSolutions(sparcWalkInspectionState.possibleSolutions);
      }

      if (sparcWalkInspectionState.miscellaneousObservations) {
        setMiscNotes(sparcWalkInspectionState.miscellaneousObservations);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWalk, dispatch]);

  useEffect(() => {
    if (selectedWalk?.walkData) {
      setShowValidation(false);

      // Use our syncCompleteWalkDataToInspection function to ensure all data is copied
      syncCompleteWalkDataToInspection(selectedWalk.walkData);

      // Additional UI state management can still be done here
      const walkData = selectedWalk.walkData;

      // Check if this is a completed SPARC walk and update walkState
      if (walkData.status?.toLowerCase() === "completed") {
        setWalkState("completed");
        // Sync with Redux
        dispatch(sparcWalkInspectionActions.setWalkState("completed"));
        // This will ensure all fields are read-only via the isReadOnly prop
      }

      // Keep these direct UI state updates separate from the Redux store
      // as they're specifically for the component's UI state
      if (walkData.conversedWith) {
        const employeeMatch = walkData.conversedWith.split(" ");
        setSelectedEmployee({
          id: employeeMatch[employeeMatch.length - 1] || "",
          displayText: walkData.conversedWith,
        });
      } else {
        setSelectedEmployee(null);
      }

      // Handle question mappings
      if (walkData.questionsMapping?.length) {
        const questionIds = walkData.questionsMapping.map(
          (question: { toolId: number }) => String(question.toolId)
        );
        setSelectedQuestionIds(questionIds);
      } else {
        setSelectedQuestionIds([]);
      }

      // Set all text fields with proper field mapping and null coalescing
      // Use the exact field names from the API response
      setExpectedAnswers(walkData.sparcExpectedAnswers || "");
      setObservations(walkData.notes || "");
      setPossibleSolutions(walkData.sparcSolutions || "");
      setMiscNotes(walkData.notes || "");
      setManagerSignature(walkData.managerSignature || "");

      // Update walk state based on the current status
      const walkStatus =
        walkData.status?.toLowerCase() || walkData.status?.toLowerCase();
      const newWalkState =
        walkStatus === "completed" ? "completed" : "in-progress";
      setWalkState(newWalkState);
      // Sync with Redux
      dispatch(sparcWalkInspectionActions.setWalkState(newWalkState));

      // Update selected walk in Redux store if needed
      if (walkData.id && (!selectedWalk || selectedWalk.id !== walkData.id)) {
        dispatch(
          setSelectedWalk({
            ...walkData,
            id: walkData.id,
            walkData: {
              ...walkData,
              // Preserve field mappings with proper casing
              sparcExpectedAnswers: walkData.sparcExpectedAnswers || "",
              sparcSolutions: walkData.sparcSolutions || "",

              notes: walkData.notes || "",
              managerSignature: "", // Set empty string instead of null
              // Ensure the images and descriptions are preserved
              miscellImage: walkData.miscellImage || null,
              miscellImage2: walkData.miscellImage2 || null,
              miscellImageDesc: walkData.miscellImageDesc || null,
              miscellImage2Desc: walkData.miscellImage2Desc || null,
              updatedImg1: walkData.updatedImg1 || false,
              updatedImg2: walkData.updatedImg2 || false,
              updatedManagerSign: false,
            },
          })
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWalkData, selectedWalk?.id, selectedWalk, dispatch]);

  // Add function to sync all data from selectedWalk to sparcWalkInspection
  const syncCompleteWalkDataToInspection = (walkData: any) => {
    if (!walkData) {
      return;
    }

    // Sync behavior mappings
    if (walkData.toolsMapping?.length) {
      // Filter out null or zero toolIds - Zero is used as a placeholder in the API
      const behaviorIds = walkData.toolsMapping
        .filter(
          (tool: { toolId: number }) =>
            tool.toolId !== null && tool.toolId !== 0
        )
        .map((tool: { toolId: number }) => String(tool.toolId));

      // Log the filtered behavior IDs to confirm the filtering is working
      console.log(
        "Filtered behavior IDs:",
        behaviorIds,
        "from original:",
        walkData.toolsMapping
      );

      setSelectedBehaviorIds(behaviorIds);
      dispatch(sparcWalkInspectionActions.setSelectedBehaviorIds(behaviorIds));
    }

    // Sync question mappings
    if (walkData.questionsMapping?.length) {
      const questionIds = walkData.questionsMapping.map(
        (question: { toolId: number }) => String(question.toolId)
      );
      setSelectedQuestionIds(questionIds);
      dispatch(sparcWalkInspectionActions.setSelectedQuestionIds(questionIds));
    }

    console.log("walkData.sparcWalkImages:", walkData);
    // Sync question images if present
    if (walkData.sparcWalkImages?.length) {
      // Clear any existing images first
      dispatch(sparcWalkInspectionActions.clearImages());

      // Add each image to the Redux store
      walkData.sparcWalkImages.forEach((img: any) => {
        console.log("Syncing image:", img);
        // If ImageBase64 is a URL (starts with http/https), treat as imageUrl
        // const isUrl = typeof img.ImageBase64 === "string" && img.ImageBase64.startsWith("http");
        dispatch(
          sparcWalkInspectionActions.updateSparcWalkImage({
            imageId: img.id || String(Date.now()),
            imageBase64: img.ImageBase64 || img.imageUrl || null,
            imagePreview: img.ImageBase64 || img.imageUrl || null,
            description: img.description || "",
            observationId: String(img.observationId || ""),
          })
        );
      });
    }

    // Sync text fields
    if (walkData.sparcExpectedAnswers) {
      setExpectedAnswers(walkData.sparcExpectedAnswers);
      dispatch(
        sparcWalkInspectionActions.setExpectedAnswers(
          walkData.sparcExpectedAnswers
        )
      );
    }

    if (walkData.notes) {
      setObservations(walkData.notes);
      dispatch(sparcWalkInspectionActions.setObservations(walkData.notes));
    }

    if (walkData.sparcSolutions) {
      setPossibleSolutions(walkData.sparcSolutions);
      dispatch(
        sparcWalkInspectionActions.setPossibleSolutions(walkData.sparcSolutions)
      );
    }

    // Sync employee data
    if (walkData.conversedWith) {
      // Extract GPID from conversedWith if available
      const employeeMatch = walkData.conversedWith.split(" ");
      const employeeId = employeeMatch[employeeMatch.length - 1] || "";
      setSelectedEmployee({
        id: employeeId,
        displayText: walkData.conversedWith,
      });
      dispatch(sparcWalkInspectionActions.setSelectedEmployeeId(employeeId));
    }

    if (walkData.managerSignature) {
      setManagerSignature(walkData.managerSignature);
      dispatch(
        sparcWalkInspectionActions.setManagerSignature({
          fileName: null,
          imageUrl: walkData.managerSignature,
          isNewUpload: false, // Assume this is not a new upload
        })
      );
    }

    if (walkData.miscellImage || walkData.miscellImage2) {
      // Sync miscellaneous observation images
      if (walkData.miscellImage) {
        dispatch(
          sparcWalkInspectionActions.setMiscObservationImage({
            index: 0,
            image: {
              imageUrl: walkData.miscellImage || "",
              fileName: walkData.miscellImageFileName || "",
              description: walkData.miscellImageDesc || "",
              index: 0,
            },
          })
        );
      }
      if (walkData.miscellImage2) {
        dispatch(
          sparcWalkInspectionActions.setMiscObservationImage({
            index: 1,
            image: {
              imageUrl: walkData.miscellImage2 || "",
              fileName: walkData.miscellImage2FileName || "",
              description: walkData.miscellImage2Desc || "",
              index: 1,
            },
          })
        );
      }
    }
    // Sync additional metadata
    dispatch(
      sparcWalkInspectionActions.setWalkState(
        walkData.status?.toLowerCase() === "completed"
          ? "completed"
          : walkData.status?.toLowerCase() === "in progress"
          ? "in-progress"
          : "new"
      )
    );

    // Store complete API response data in sparcWalkInspection slice
    dispatch(sparcWalkInspectionActions.setCompleteWalkData(walkData));
  };

  const handleSubmit = async (status: string): Promise<void> => {
    // Always show validation for both save and complete
    setShowValidation(true);

    // Employee selection is mandatory for both save and complete
    // Check both local state and Redux state to handle async updates on mobile
    const sparcWalkInspectionState = store.getState().sparcWalkInspection;
    const hasEmployeeInState = selectedEmployee?.id && selectedEmployee.id.trim() !== "";
    const hasEmployeeInRedux = sparcWalkInspectionState.selectedEmployeeId && 
                                sparcWalkInspectionState.selectedEmployeeId.trim() !== "";
    
    if (!hasEmployeeInState && !hasEmployeeInRedux) {
      showStatusMessage(
        "error",
        "Employee Conversed field is mandatory. Please select an employee."
      );
      return;
    }

    const managerSignatureUrl =
      sparcWalkInspectionState.managerSignatureImageUrl;
    const existingSignature = selectedWalk?.walkData?.managerSignature;

    // Validate manager signature only for completing the walk (when not saving)
    const hasSignature = !!managerSignatureUrl || !!existingSignature;
    if (status !== "save" && !hasSignature) {
      showStatusMessage(
        "error",
        "Manager signature is required to complete the walk. Please add a signature."
      );
      scrollToTop(); // Make sure user sees the error
      return;
    }

    try {
      setIsLoading(true);

      const walkId = selectedWalk?.id || selectedWalkData?.id || null;
      const isNewWalk = !walkId; // It's a new walk if we don't have an ID
      const isInProgress =
        selectedWalk?.walkData?.status?.toLowerCase() === "in progress";

      // Get the latest data from sparcWalkInspection Redux slice
      const sparcWalkInspectionState = store.getState().sparcWalkInspection;

      // Always use the complete walk data from our Redux store when available
      // This ensures we have access to all API response data
      const existingWalkData =
        sparcWalkInspectionState.completeWalkData ||
        selectedWalk?.walkData ||
        {};

      // Always prioritize the current form state (what user has changed) for these fields
      // These are values user may have updated during this session
      const finalObservations = observations;
      const finalExpectedAnswers = expectedAnswers;
      const finalPossibleSolutions = possibleSolutions;

      // For behavior IDs and question IDs, prioritize the current selections from the form
      // as they may have been updated by the user
      const finalBehaviorIds = selectedBehaviorIds;
      const finalQuestionIds = selectedQuestionIds;

      // Extract miscellaneous observation images from Redux state
      const miscObservationImages =
        sparcWalkInspectionState.miscObservationImages || [];

      // Process images to get the URLs and descriptions for API payload
      let miscImage = "";
      let miscImage2 = "";
      let miscImageDesc = "";
      let miscImage2Desc = "";

      // For in-progress walks, use existing image data if no new ones are selected
      if (isInProgress && !miscObservationImages.length) {
        miscImage = existingWalkData.miscellImage || "";
        miscImage2 = existingWalkData.miscellImage2 || "";
        miscImageDesc = existingWalkData.miscellImageDesc || "";
        miscImage2Desc = existingWalkData.miscellImage2Desc || "";
      }

      if (miscObservationImages.length > 0 && miscObservationImages[0]) {
        miscImage = miscObservationImages[0].imageUrl || "";
        miscImageDesc = miscObservationImages[0].description || "";
      }

      if (miscObservationImages.length > 1 && miscObservationImages[1]) {
        miscImage2 = miscObservationImages[1].imageUrl || "";
        miscImage2Desc = miscObservationImages[1].description || "";
      }

      // Get manager signature data
      const managerSignature =
        isInProgress && !sparcWalkInspectionState.managerSignatureImageUrl
          ? existingWalkData.managerSignature || ""
          : sparcWalkInspectionState.managerSignatureImageUrl || "";
      const isManagerSignatureUpdated =
        sparcWalkInspectionState.isManagerSignatureUpdated;

      // Get question images from Redux state
      const questionImages = sparcWalkInspectionState.images || [];

      console.log(
        "Question/behavior images before formatting:",
        JSON.stringify(questionImages, null, 2)
      );

      // Format images for the API payload
      // Make sure we're using the correct observation IDs (question/behavior IDs)
      const formattedImages = questionImages.map((img) => {
        // Parse the observationId to a number for the API
        // If parsing fails, log an error and use -1 as a fallback
        let parsedObservationId: number;
        try {
          parsedObservationId = parseInt(img.observationId, 10);
          if (isNaN(parsedObservationId)) {
            console.error(`Invalid observationId found: ${img.observationId}`);
            parsedObservationId = -1;
          }
        } catch (error) {
          console.error(
            `Error parsing observationId: ${img.observationId}`,
            error
          );
          parsedObservationId = -1;
        }

        return {
          ImageId: img.imageId,
          ImageBase64: img.imageBase64 || img.imagePreview,
          Description: img.description,
          ObservationId: parsedObservationId,
        };
      });

      console.log(
        "Formatted images for API payload:",
        JSON.stringify(formattedImages, null, 2)
      );

      // Include existing images that might not be in the current state
      const existingImages = existingWalkData.sparcWalkImages || [];

      // Combine existing and new images, prioritizing new ones
      const mergedImages = [...formattedImages];

      // Get employee display text, checking both local state and Redux for mobile compatibility
      let conversedWithText = selectedEmployee?.displayText || "";
      if (!conversedWithText && sparcWalkInspectionState.selectedEmployeeId) {
        // Find employee from formatted options using Redux ID if local state not yet updated
        const employeeFromRedux = formattedEmployeeOptions.find(
          emp => emp.id === sparcWalkInspectionState.selectedEmployeeId
        );
        conversedWithText = employeeFromRedux?.displayText || "";
      }

      const payload = {
        oneSubmission: !isNewWalk, // false for new walks, true for updating existing walks
        selectedRecord: walkId
          ? {
              id: walkId,
              __typename: "SparcWalk",
            }
          : null,
        walkData: {
          location: selectedLocation?.displayText || "",
          walkType: "SPARC Walk",
          period: periodWeek?.data?.period ?? 0,
          week: periodWeek?.data?.week ?? 0,
          zone: selectedZone.displayText,
          conversedWith: conversedWithText,
          managerSignature: managerSignature, // Use signature from Redux
          miscellImage: miscImage, // Use misc image from Redux
          miscellImage2: miscImage2, // Use misc image 2 from Redux
          miscellImageDesc: miscImageDesc, // Use misc image description from Redux
          miscellImage2Desc: miscImage2Desc, // Use misc image 2 description from Redux
          updatedImg1: miscImage !== (existingWalkData.miscellImage || ""),
          updatedImg2: miscImage2 !== (existingWalkData.miscellImage2 || ""),
          updatedManagerSign: isManagerSignatureUpdated,
          notes: finalObservations,
          sparcSolutions: finalPossibleSolutions,
          sparcCategorySelected: selectedCategory.displayText,
          sparcExpectedAnswers: finalExpectedAnswers,
          created: isNewWalk
            ? getLocalTimestamp()
            : existingWalkData.created,
          createdBy: isNewWalk
            ? userEmail || ""
            : existingWalkData.createdBy || "",
          modified: getLocalTimestamp(),
          modifiedBy: userEmail || "",
          status: status === "save" ? "In Progress" : "Completed",
        },
        // Always include toolsMapping and questionsMapping as arrays
        toolsMapping: finalBehaviorIds.map((id) => ({
          toolId: parseInt(id, 10),
        })),
        questionsMapping: finalQuestionIds.map((id) => ({
          toolId: parseInt(id, 10),
        })),
        // Include both existing and newly uploaded images
        sparcWalkImages: mergedImages,
      };

      console.log(
        "Submitting walk with payload:",
        JSON.stringify(payload, null, 2)
      );
      console.log(
        "Submitting walk with existingWalkData:",
        JSON.stringify(existingWalkData, null, 2)
      );

      console.log(
        "Walk state info:",
        JSON.stringify(
          {
            isNewWalk,
            isInProgress,
            walkState,
            status,
          },
          null,
          2
        )
      );

      // Debug logs for misc observations and manager signature
      console.log(
        "sparcWalkInspection.miscObservationImages:",
        JSON.stringify(sparcWalkInspectionState.miscObservationImages, null, 2)
      );
      console.log(
        "sparcWalkInspection.managerSignatureImageUrl:",
        sparcWalkInspectionState.managerSignatureImageUrl
      );
      console.log(
        "sparcWalkInspection.isManagerSignatureUpdated:",
        sparcWalkInspectionState.isManagerSignatureUpdated
      );

      const api = createApiClient();
      const submitResponse = await apiClient(
        api,
        "/sparc-walks/submit",
        "POST",
        payload
      );

      // If API returned an explicit error shape or falsy response, show centralized overlay with friendly message
      if (!submitResponse || submitResponse?.status === 'error' || submitResponse?.success === false) {
        const overlayMsg = (submitResponse && (submitResponse.message || submitResponse.error)) || 'Unable to submit walk. Please try again.';
        try {
          handleSparcWalkErrorWithFriendlyMessage(
            SPARC_WALK_API_NAMES.SUBMIT_WALK,
            overlayMsg,
            true,
            { status, category: selectedCategory?.displayText, zone: selectedZone?.displayText, response: submitResponse }
          );
        } catch (e) {
          console.error('Failed to show SPARC walk submit overlay:', e);
        }
        // Throw to fall back to existing error handling flow
        throw new Error(overlayMsg);
      }

      if (!submitResponse) {
        throw new Error("Failed to save walk - no response from server");
      }

      // Get the ID of the walk that was submitted
      const submittedWalkId =
        submitResponse.walkId || selectedWalkData?.id || 0;

      // Create success message
      const successMsg = `Walk ${
        status === "save" ? "saved" : "completed"
      } successfully`;

      // Show local status message
      showStatusMessage("success", successMsg);

      // Set success message in Redux state for display in the SPARC Walk dashboard
      dispatch(sparcWalkInspectionActions.setSuccessMessage(successMsg));

      // If onSubmitSuccess callback is provided, call it
      if (onSubmitSuccess) {
        onSubmitSuccess(submittedWalkId, status);
      }

      // Reset both the inspection data and selectedWalk in the Redux store to prepare for next entry
      dispatch(sparcWalkInspectionActions.resetBothInspectionAndSelectedWalk());

      // For both save and complete, navigate back to dashboard
      onBackPress?.();

      // Set a timeout to clear the success message after 5 seconds
      setTimeout(() => {
        dispatch(sparcWalkInspectionActions.setSuccessMessage(null));
      }, 5000);
    } catch (error: any) {
      const errorMessage =
        error?.message || "Failed to save walk. Please try again.";
      showStatusMessage("error", errorMessage);
      console.error("Error submitting walk:", error);
      
      // Use centralized error handler with modal overlay
      try {
        const overlayMsg = error?.response?.data?.message || errorMessage || 'Unable to submit walk. Please try again.';
        handleSparcWalkErrorWithFriendlyMessage(
          SPARC_WALK_API_NAMES.SUBMIT_WALK,
          overlayMsg,
          true, // Show modal overlay
          {
            status,
            category: selectedCategory?.displayText,
            zone: selectedZone?.displayText,
            originalError: error,
          }
        );
      } catch (e) {
        console.error('Failed to dispatch SPARC walk submit overlay in catch:', e);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Cast the zone and category to match the expected type
  const formattedSelectedZone: { id: string; displayText: string } = {
    id: String(selectedZone.id),
    displayText: selectedZone.displayText,
  };

  const formattedSelectedCategory: { id: string; displayText: string } = {
    id: String(selectedCategory.id),
    displayText: selectedCategory.displayText,
  };

  // Add a selector for success message from Redux state
  const successMessageFromRedux = useSelector(
    (state: RootState) => state.sparcWalkInspection.successMessage
  );

  // Add a selector for manager signature from Redux state
  const managerSignatureFromRedux = useSelector(
    (state: RootState) => state.sparcWalkInspection.managerSignatureImageUrl
  );

  // Effect to scroll to top when success message appears and clear it after a delay
  useEffect(() => {
    if (successMessageFromRedux) {
      // Scroll to top when success message appears
      if (scrollViewRef.current) {
        scrollViewRef.current.scrollTo({ y: 0, animated: true });
      }

      // Set local status message to match Redux success message
      showStatusMessage("success", successMessageFromRedux);

      // Auto-clear the success message after 5 seconds
      const timer = setTimeout(() => {
        dispatch(sparcWalkInspectionActions.setSuccessMessage(null));
      }, 5000);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successMessageFromRedux, dispatch]);

  // Effect to scroll to top when validation errors appear
  useEffect(() => {
    if (statusMessage && statusMessage.type === "error") {
      scrollToTop();
    }
  }, [statusMessage]);
  
  // Handle scroll events for web
  useEffect(() => {
    if (Platform.OS === "web") {
      const handleWebScroll = () => {
        const shouldShow = window.scrollY > 200;
        
        if (shouldShow !== showScrollToTop) {
          setShowScrollToTop(shouldShow);
          
          Animated.timing(scrollButtonOpacity, {
            toValue: shouldShow ? 1 : 0,
            duration: 200,
            useNativeDriver: true,
          }).start();
        }
      };

      window.addEventListener('scroll', handleWebScroll);
      return () => window.removeEventListener('scroll', handleWebScroll);
    }
  }, [showScrollToTop, scrollButtonOpacity]);

  // Pass walkState to child components to control disabled state
  if (isLoading) {
    return (
      <View
        style={[
          styles.container,
          isDark && { backgroundColor: colors.background.page },
        ]}
      >
        <SparcWalkInspectionHeader
          title={`SPARC Walk: ${selectedCategory?.displayText}`}
          subtitle={`Zone: ${selectedZone?.displayText}`}
          onReturn={onBackPress}
          onNavigateHome={onNavigateHome}
          isReadOnly={isReadOnly}
        />
        <LHLoader />
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      style={[
        styles.keyboardView,
        isDark && { backgroundColor: colors.background.page },
      ]}
    >
      <ScrollView
        ref={scrollViewRef}
        style={isDark ? { backgroundColor: colors.background.page } : undefined}
        contentContainerStyle={[
          styles.container,
          isDark && { backgroundColor: colors.background.page },
        ]}
        keyboardShouldPersistTaps="handled"
        onScroll={Platform.OS !== "web" ? handleScroll : undefined}
        scrollEventThrottle={16}
      >
        <SparcWalkInspectionHeader
          title={`SPARC Walk: ${selectedCategory?.displayText}`}
          subtitle={`Zone: ${selectedZone?.displayText}`}
          onReturn={onBackPress}
          onNavigateHome={onNavigateHome}
          isReadOnly={isReadOnly}
        />

        {statusMessage && (
          <View
            style={[
              styles.statusMessage,
              statusMessage.type === "error"
                ? styles.errorMessage
                : styles.successMessage,
              isDark && {
                backgroundColor: colors.background.surface,
                borderColor: colors.border.default,
                borderLeftColor:
                  statusMessage.type === "error"
                    ? colors.status.error
                    : colors.status.success,
              },
            ]}
          >
            <Text
              style={[
                styles.statusText,
                statusMessage.type === "error"
                  ? styles.errorText
                  : styles.successText,
                isDark && { color: colors.text.primary },
              ]}
            >
              {statusMessage.message}
            </Text>
          </View>
        )}
        <SparcWalkInspectionActivity
          title="Select any behaviors you expect to see"
          options={behaviourData}
          selectedIds={selectedBehaviorIds}
          onSelect={(id) => {
            if (isReadOnly) {
              return;
            }

            const newSelectedIds = selectedBehaviorIds.includes(id)
              ? selectedBehaviorIds.filter((x) => x !== id)
              : [...selectedBehaviorIds, id];

            // Update local state
            setSelectedBehaviorIds(newSelectedIds);

            // Update Redux store
            dispatch(
              sparcWalkInspectionActions.setSelectedBehaviorIds(newSelectedIds)
            );
          }}
          isDisabled={isReadOnly}
          error={false} // No validation requirement for behaviors
        />

        <SparcWalkInspectionQuestions
          title="Select the questions you want to ask"
          options={questionItems}
          selectedIds={selectedQuestionIds}
          onSelect={(id) => {
            if (isReadOnly) {
              return;
            }

            const newSelectedIds = selectedQuestionIds.includes(id)
              ? selectedQuestionIds.filter((x) => x !== id)
              : [...selectedQuestionIds, id];

            // Update local state
            setSelectedQuestionIds(newSelectedIds);

            // Update Redux store
            dispatch(
              sparcWalkInspectionActions.setSelectedQuestionIds(newSelectedIds)
            );
          }}
          isDisabled={isReadOnly}
          error={false}
        />
        <SparcWalkInspectionExpectedAnswers
          value={expectedAnswers}
          onChange={(text) => {
            if (isReadOnly) {
              return;
            }

            // Update local state
            setExpectedAnswers(text);

            // Update Redux store
            dispatch(sparcWalkInspectionActions.setExpectedAnswers(text));
          }}
          isDisabled={isReadOnly}
          isCompleted={walkState === "completed"}
          error={false}
        />

        <SparcWalkInspectionCompleteSection />

        <SparcWalkInspectionObservations
          value={observations}
          onChange={(text) => {
            if (isReadOnly) {
              return;
            }

            // Update local state
            setObservations(text);

            // Update Redux store
            dispatch(sparcWalkInspectionActions.setObservations(text));
          }}
          isDisabled={isReadOnly}
          isCompleted={walkState === "completed"}
          error={false}
        />

        <SparcWalkInspectionPosibleSolutions
          value={possibleSolutions}
          onChange={(text) => {
            if (isReadOnly) {
              return;
            }

            // Update local state
            setPossibleSolutions(text);

            // Update Redux store
            dispatch(sparcWalkInspectionActions.setPossibleSolutions(text));
          }}
          isDisabled={isReadOnly}
          isCompleted={walkState === "completed"}
          error={false}
        />

        <SparcWalkInspectionMiscellaneous
          employeeOptions={formattedEmployeeOptions}
          selectedEmployee={selectedEmployee}
          setSelectedEmployee={(emp) => {
            if (isReadOnly) {
              return;
            }

            // Update local state
            setSelectedEmployee(emp);

            // Update Redux store with the employee ID
            if (emp && emp.id) {
              dispatch(
                sparcWalkInspectionActions.setSelectedEmployeeId(emp.id)
              );
            } else {
              dispatch(sparcWalkInspectionActions.setSelectedEmployeeId(null));
            }
          }}
          isDisabled={isReadOnly}
          error={(() => {
            if (!showValidation) return false;
            const sparcWalkInspectionState = store.getState().sparcWalkInspection;
            const hasEmployeeInState = selectedEmployee?.id && selectedEmployee.id.trim() !== "";
            const hasEmployeeInRedux = sparcWalkInspectionState.selectedEmployeeId && 
                                        sparcWalkInspectionState.selectedEmployeeId.trim() !== "";
            return !hasEmployeeInState && !hasEmployeeInRedux;
          })()}
          errorMessage={
            showValidation && (() => {
              const sparcWalkInspectionState = store.getState().sparcWalkInspection;
              const hasEmployeeInState = selectedEmployee?.id && selectedEmployee.id.trim() !== "";
              const hasEmployeeInRedux = sparcWalkInspectionState.selectedEmployeeId && 
                                          sparcWalkInspectionState.selectedEmployeeId.trim() !== "";
              return !hasEmployeeInState && !hasEmployeeInRedux;
            })()
              ? "Employee Conversed field is mandatory"
              : ""
          }
        />

        {/* Get manager signature for validation */}
        <SparcWalkSignature
          isDisabled={isReadOnly}
          error={
            showValidation &&
            !managerSignatureFromRedux &&
            !selectedWalk?.walkData?.managerSignature
          }
        />

        <SparcWalkInspectionAction
          selectedEmployee={selectedEmployee}
          selectedZone={formattedSelectedZone}
          selectedCategory={formattedSelectedCategory}
          showStatusMessage={showStatusMessage}
          onSubmit={handleSubmit}
          walkState={walkState}
          isDisabled={isDisabled}
          selectedBehaviorIds={selectedBehaviorIds}
          selectedQuestionIds={selectedQuestionIds}
          observations={observations}
          possibleSolutions={possibleSolutions}
          sparcExpectedAnswers={expectedAnswers}
          walkData={selectedWalk?.walkData || {}}
          sparcWalkImages={selectedWalk?.walkData?.sparcWalkImages || []}
        />

        {/* <Text style={styles.footer}>
          © 2025 PepsiCo | SPARC Walk Inspection
        </Text> */}
      </ScrollView>

      {/* Scroll to Top Button */}
      <Animated.View
        ref={scrollToTopButtonRef}
        testID="scroll-to-top-button"
        style={[
          styles.scrollToTopButton,
          Platform.OS === "web" ? styles.scrollToTopButtonWeb : {},
          {
            opacity: scrollButtonOpacity,
            transform: [
              {
                scale: scrollButtonOpacity.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0.8, 1],
                }),
              },
            ],
          },
        ]}
        pointerEvents={showScrollToTop ? "auto" : "none"}
      >
        <TouchableOpacity
          style={[
            styles.scrollToTopTouchable,
            isDark && {
              backgroundColor: colors.background.elevated,
              borderColor: colors.border.default,
            },
          ]}
          onPress={scrollToTop}
          activeOpacity={0.7}
        >
          {/* Arrow up icon */}
          <View style={styles.arrowIcon}>
            <Text
              style={[
                styles.arrowText,
                isDark && { color: colors.brand.primary },
              ]}
            >
              ↑
            </Text>
          </View>
        </TouchableOpacity>
      </Animated.View>

      {/* Centralized Error Modal */}
      <SparcWalkErrorModal />
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  keyboardView: {
    flex: 1,
  },
  container: {
    backgroundColor: "#F5F9FF",
    paddingBottom: 50,
    flexGrow: 1,
    alignItems: "stretch",
    paddingHorizontal: Platform.OS === "web" ? 24 : 16,
  },
  footer: {
    textAlign: "center",
    marginTop: 40,
    color: "#FFFFFF",
    fontSize: 12,
    fontFamily: FontFamily.regular,
  },
  errorText: {
    color: "#721c24",
    fontSize: 14,
    fontFamily: FontFamily.regular,
  },
  successText: {
    color: "#155724",
    fontSize: 14,
    fontFamily: FontFamily.regular,
  },
  statusText: {
    flex: 1,
    fontSize: 14,
    fontFamily: FontFamily.regular,
  },
  statusMessage: {
    padding: 14,
    margin: 16,
    borderRadius: 6,
    flexDirection: "row",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 3,
    elevation: 5,
    marginTop: 10,
    marginBottom: 20,
  },
  successMessage: {
    backgroundColor: "#d4edda",
    borderColor: "#c3e6cb",
    borderWidth: 1,
    borderLeftWidth: 5,
    borderLeftColor: "#28a745",
  },
  errorMessage: {
    backgroundColor: "#f8d7da",
    borderColor: "#f5c6cb",
    borderWidth: 1,
    borderLeftWidth: 5,
    borderLeftColor: "#dc3545",
  },
  scrollToTopButton: {
    position: "absolute",
    bottom: Platform.OS === "ios" ? 100 : 80,
    right: 20,
    zIndex: 1000,
  },
  scrollToTopButtonWeb: {
    position: "absolute", 
    bottom: 20,
    right: 20,
  },
  scrollToTopTouchable: {
    backgroundColor: "#FFFFFF",
    borderRadius: 28,
    width: 56,
    height: 56,
    justifyContent: "center",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 4.65,
    elevation: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
  },
  arrowIcon: {
    width: 20,
    height: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  arrowText: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#0066CC",
    lineHeight: 24,
    fontFamily: FontFamily.bold,
  },
});

export default SparcWalkInspectionScreen;
